import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FlatInteractionModelData } from 'types/shared'
import { Token } from 'types/workflow'
import {
  DecisionModel,
  DMNAction,
  OutputEntries,
  OutputValue,
  evaluateDecisionModel
} from 'modules/dmn'
import { DecisionModelActionAutomation } from '../useAutomation'
import { flatValuesToOldActivities } from '../../utils/flatValuesToOldActivities'
import useExecuteDMNActions from './useExecuteDMNActions'

function isDMNAction(value: unknown): value is DMNAction {
  return (
    typeof value === 'object' &&
    value !== null &&
    'action_type' in value &&
    !Array.isArray(value)
  )
}

function flattenOutputValue(value: OutputValue): DMNAction[] {
  if (Array.isArray(value)) return value
  return [value]
}

function normalizeOutputEntriesToActions(
  output: OutputEntries | OutputEntries[]
): DMNAction[] {
  if (Array.isArray(output)) {
    return output.flatMap((entry) =>
      normalizeOutputEntriesToActions(entry as OutputEntries)
    )
  }

  if (isDMNAction(output)) return [output]

  return Object.values(output as { [outputUuid: string]: OutputValue }).flatMap(
    flattenOutputValue
  )
}

type UseDecisionModelAutomationProps = {
  decisionModels: DecisionModel[]
  activityValues: FlatInteractionModelData['activity_values']
  process?: Token
}

export default function useDecisionModelAutomation({
  decisionModels,
  activityValues,
  process
}: UseDecisionModelAutomationProps) {
  const [searchParams] = useSearchParams()
  const { executeActions } = useExecuteDMNActions()

  const hasPendingDecisionModels = decisionModels.some((model) =>
    Object.values(model.data).some(
      (source) =>
        source.source === 'activity_value' && !activityValues[source.identifier]
    )
  )

  const executeDecisionModelAutomation = useCallback(
    (automation: DecisionModelActionAutomation) => {
      const decisionModel = decisionModels.find(
        (m) => m.uuid === automation.automation_data.decision_model_uuid
      )

      if (!decisionModel) return

      const activities = flatValuesToOldActivities(activityValues)

      const queryParams = Object.fromEntries(searchParams.entries())
      const processDataContainer =
        process?.execution_state?.data_container ?? {}
      const context = { activities, queryParams, processDataContainer }

      const output = evaluateDecisionModel(decisionModel, context)

      executeActions(normalizeOutputEntriesToActions(output))
    },
    [decisionModels, activityValues, searchParams, process, executeActions]
  )

  return {
    hasPendingDecisionModels,
    executeDecisionModelAutomation
  }
}
